import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import { change } from 'redux-form';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import DocumentTitle from 'react-document-title';

import type {
  GlobalState,
  User,
  UserStatus,
  UserTitle,
  UserRole,
  Property,
} from '../App/types';
import * as editUserActions from './actions';
import * as userActions from '../CreateUser/actions';
import * as appActions from '../App/actions';
import EditUserForm from './EditUserForm';
import messages from './messages';
import confirm from '../../components/ConfirmDialogModal';

type Props = {
  user: User,
  selectedProperties: Array<string>,
  userStatuses: Array<UserStatus>,
  userTitles: Array<UserTitle>,
  userRoles: Array<UserRole>,
  properties: Array<Property>,
  userId: string,
  nonePropertiesSelected: boolean,
  hideMultiselect: boolean,
  showSuccessMessage: boolean,
  showErrorMessage: boolean,
  errorMessage: string,
  organizationId: string,
  isSubmitting: boolean,
};

type InjectedProps = {
  userActions: Object,
  editUserActions: Object,
  intl: any,
  history: any,
};

export class EditUserPage extends Component<Props & InjectedProps> {
  componentDidMount() {
    this.props.editUserActions.getUser(this.props.userId);
    this.props.userActions.getAllUserStatuses();
    this.props.userActions.getAllUserTitles();
    this.props.userActions.getAllUserRoles();
    this.props.userActions.getAllProperties();
    this.props.appActions.getAllPropertyClasses();
    this.fetchPropertyGroups();
  }

  fetchPropertyGroups = async () => {
    try {
      const PropertyGroupService =
        require('../../services/propertyGroupService').default;
      const propertyGroupService = new PropertyGroupService();
      const propertyGroups = await propertyGroupService.getAllPropertyGroups(
        this.props.organizationId,
      );
      this.props.editUserActions.setPropertyGroupsData(propertyGroups || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching property groups:', error);
    }
  };

  generateUserStatuses() {
    const userStatuses = this.props.userStatuses.map((userStatus) => ({
      value: userStatus.id,
      text: userStatus.name,
      disabled: false,
    }));
    userStatuses.unshift({
      value: 'default',
      text: this.props.intl.formatMessage(messages.chooseOption),
      disabled: true,
    });
    return userStatuses;
  }

  generateUserTitles() {
    const userTitles = this.props.userTitles.map((userTitle) => ({
      value: userTitle.id,
      text: userTitle.name,
      disabled: false,
    }));
    userTitles.unshift({
      value: 'default',
      text: this.props.intl.formatMessage(messages.chooseOption),
      disabled: true,
    });
    return userTitles;
  }

  generateUserRoles() {
    const userRoles = this.props.userRoles.map((userRole) => ({
      value: userRole.id,
      text: userRole.name,
      disabled: false,
    }));
    userRoles.unshift({
      value: 'default',
      text: this.props.intl.formatMessage(messages.chooseOption),
      disabled: true,
    });
    return userRoles;
  }

  generateProperties() {
    return this.props.properties.map((property) => ({
      value: property.id,
      label: property.name,
    }));
  }

  handleChange = (property: string) => {
    this.props.editUserActions.setSelectedProperties(property);
  };

  handlePropertiesSelectedError = (newState: boolean) => {
    this.props.editUserActions.showNonePropertiesSelectedError(newState);
  };

  handleSubmit = (user: any) => {
    const flag = this.props.flags.adminEnhancementsUsersAssigntopropertytype;
    const hasDefaultGroupSelected =
      user?.hasAffordableGroup ||
      user?.hasCommercialGroup ||
      user?.hasConventionalGroup;
    const hasNoProperties =
      !user?.properties?.length || user?.properties?.length === 0;
    const hasNoPropertyGroups =
      !user?.propertyGroups?.length || user?.propertyGroups?.length === 0;
    const noSelection = flag
      ? !user.allProperties &&
        hasNoProperties &&
        hasNoPropertyGroups &&
        !hasDefaultGroupSelected
      : !user.allProperties && user?.properties?.length === 0;
    if (noSelection) {
      this.handlePropertiesSelectedError(true);
    } else {
      this.props.editUserActions.editUser(user, this.props.user.id);
      this.handlePropertiesSelectedError(false);
    }
  };

  handleCancel = () => {
    confirm(this.props.intl.formatMessage(messages.cancelConfirmation), {
      intl: this.props.intl,
    }).then(() => {
      this.props.history.replace('/manage-users');
      this.handlePropertiesSelectedError(false);
    });
  };

  toggleMultiselect = (ev: any, value: boolean) => {
    this.props.editUserActions.toggleMultiselect(value);
  };

  handleAllPropertiesSelection = (val: any) => {
    this.props.editUserActions.setUserAllProperties(val);
  };

  handlePropertySelectionChange = (properties: Array<string>) => {
    this.props.editUserActions.setSelectedProperties(properties);
  };

  handleDefaultGroups = (groups: Array<string>) => {
    // filter out predefined values affordable/conventional/commercial
    const hasAffordableGroup = groups.some((gid) => gid === 'AffordableGroup');
    const hasConventionalGroup = groups.some(
      (gid) => gid === 'ConventionalGroup',
    );
    const hasCommercialGroup = groups.some((gid) => gid === 'CommercialGroup');

    const groupIdsToFilterOut = [
      'AffordableGroup',
      'ConventionalGroup',
      'CommercialGroup',
    ];
    const filteredGroups = groups.filter(
      (groupId) => !groupIdsToFilterOut.includes(groupId),
    );
    this.props.reduxFormActions.change(
      'editUser',
      'propertyGroups',
      filteredGroups,
    );
    this.props.reduxFormActions.change(
      'editUser',
      'hasAffordableGroup',
      hasAffordableGroup,
    );
    this.props.reduxFormActions.change(
      'editUser',
      'hasConventionalGroup',
      hasConventionalGroup,
    );
    this.props.reduxFormActions.change(
      'editUser',
      'hasCommercialGroup',
      hasCommercialGroup,
    );
  };

  handlePropertyGroupSelectionChange = (groups: Array<string>) => {
    this.handleDefaultGroups(groups);
    this.props.editUserActions.setSelectedPropertyGroups(groups);

    // Synchronize individual properties with property groups
    this.synchronizePropertiesWithGroups(groups);
  };

  synchronizePropertiesWithGroups = (selectedGroups) => {
    const { propertyGroupsData, selectedProperties } = this.props;
    const { properties } = this.props;

    if (!propertyGroupsData || !properties) return;

    // Get properties from currently selected groups
    const propertiesFromSelectedGroups = new Set();

    // Handle custom property groups
    const customGroups = selectedGroups.filter(
      (groupId) =>
        !['AffordableGroup', 'ConventionalGroup', 'CommercialGroup'].includes(
          groupId,
        ),
    );

    customGroups.forEach((groupId) => {
      const group = propertyGroupsData.find((pg) => pg.id === groupId);
      if (group && group.propertyIds) {
        group.propertyIds.forEach((propertyId) => {
          propertiesFromSelectedGroups.add(propertyId);
        });
      }
    });

    // Handle default property type groups
    const [affordableProperties, conventionalProperties, commercialProperties] =
      this.categorizeProperties(properties);

    if (selectedGroups.includes('AffordableGroup')) {
      affordableProperties.forEach((prop) =>
        propertiesFromSelectedGroups.add(prop.id),
      );
    }
    if (selectedGroups.includes('ConventionalGroup')) {
      conventionalProperties.forEach((prop) =>
        propertiesFromSelectedGroups.add(prop.id),
      );
    }
    if (selectedGroups.includes('CommercialGroup')) {
      commercialProperties.forEach((prop) =>
        propertiesFromSelectedGroups.add(prop.id),
      );
    }

    // Get properties that were previously selected through groups but are no longer in selected groups
    const previouslySelectedGroups = this.props.selectedPropertyGroups || [];
    const unselectedGroups = previouslySelectedGroups.filter(
      (groupId) => !selectedGroups.includes(groupId),
    );

    const propertiesFromUnselectedGroups = new Set();
    unselectedGroups.forEach((groupId) => {
      if (
        ['AffordableGroup', 'ConventionalGroup', 'CommercialGroup'].includes(
          groupId,
        )
      ) {
        // Handle default groups
        if (groupId === 'AffordableGroup') {
          affordableProperties.forEach((prop) =>
            propertiesFromUnselectedGroups.add(prop.id),
          );
        } else if (groupId === 'ConventionalGroup') {
          conventionalProperties.forEach((prop) =>
            propertiesFromUnselectedGroups.add(prop.id),
          );
        } else if (groupId === 'CommercialGroup') {
          commercialProperties.forEach((prop) =>
            propertiesFromUnselectedGroups.add(prop.id),
          );
        }
      } else {
        // Handle custom groups
        const group = propertyGroupsData.find((pg) => pg.id === groupId);
        if (group && group.propertyIds) {
          group.propertyIds.forEach((propertyId) => {
            propertiesFromUnselectedGroups.add(propertyId);
          });
        }
      }
    });

    // Filter out properties that are no longer accessible through groups
    // Keep only individual properties and properties from currently selected groups
    const newSelectedProperties = selectedProperties.filter((propertyId) => {
      // Keep if it's from a currently selected group
      if (propertiesFromSelectedGroups.has(propertyId)) {
        return true;
      }
      // Keep if it's not from any unselected group (individual property)
      if (!propertiesFromUnselectedGroups.has(propertyId)) {
        return true;
      }
      // Remove if it was only accessible through an unselected group
      return false;
    });

    // Add properties from newly selected groups
    propertiesFromSelectedGroups.forEach((propertyId) => {
      if (!newSelectedProperties.includes(propertyId)) {
        newSelectedProperties.push(propertyId);
      }
    });

    this.props.editUserActions.setSelectedProperties(newSelectedProperties);
  };

  categorizeProperties = (propertyMap) => {
    // affordable includes mixed
    const affordableProperties = [];
    const conventionalProperties = [];
    const commercialProperties = [];

    propertyMap.forEach((property) => {
      if (
        property?.propertyClass?.name === 'Affordable' ||
        property?.propertyClass?.name === 'Mixed'
      ) {
        affordableProperties.push(property);
      }

      if (property?.propertyClass?.name === 'Conventional') {
        conventionalProperties.push(property);
      }

      if (
        property?.hasCommercialFloorPlans === 'ALL' ||
        property?.hasCommercialFloorPlans === 'SOME'
      ) {
        commercialProperties.push(property);
      }
    });
    return [affordableProperties, conventionalProperties, commercialProperties];
  };

  render() {
    const userStatuses = this.generateUserStatuses();
    const userTitles = this.generateUserTitles();
    const userRoles = this.generateUserRoles();
    const userProperties = this.generateProperties();
    const {
      user: {
        firstName,
        lastName,
        emailAddress,
        username,
        allProperties,
        userStatusId,
        userTitleId,
        userRoleId,
        hasAffordableGroup,
        hasCommercialGroup,
        hasConventionalGroup,
        propertyGroups,
      },
      properties: propertyList,
      propertyClasses,
      isSubmitting,
      organizationId,
      selectedPropertyGroups,
    } = this.props;
    const initialPropertyGroups = propertyGroups?.map((pg) => pg.id);
    return (
      <DocumentTitle title={this.props.intl.formatMessage(messages.title)}>
        <EditUserForm
          initialValues={{
            firstName,
            lastName,
            emailAddress,
            username,
            allProperties,
            userStatusId,
            userTitleId,
            userRoleId,
            organizationId,
            hasAffordableGroup,
            hasCommercialGroup,
            hasConventionalGroup,
            propertyGroups: initialPropertyGroups,
          }}
          intl={this.props.intl}
          userStatuses={userStatuses}
          userTitles={userTitles}
          userRoles={userRoles}
          properties={userProperties}
          propertyList={propertyList}
          selectedProperties={this.props.selectedProperties}
          selectedPropertyGroups={selectedPropertyGroups}
          hideMultiselect={this.props.hideMultiselect}
          toggleMultiselect={this.toggleMultiselect}
          handleCancel={this.handleCancel}
          onSubmit={this.handleSubmit}
          handleMultiselectChange={this.handleChange}
          handlePropertiesSelectedError={this.handlePropertiesSelectedError}
          handleAllPropertiesSelection={this.handleAllPropertiesSelection}
          nonePropertiesSelected={this.props.nonePropertiesSelected}
          isSubmitting={isSubmitting}
          propertyClasses={propertyClasses}
          organizationId={organizationId}
          allPropertiesSelected={allProperties}
          handlePropertySelectionChange={this.handlePropertySelectionChange}
          handlePropertyGroupSelectionChange={
            this.handlePropertyGroupSelectionChange
          }
          user={this.props.user}
          propertyGroupsData={this.props.propertyGroupsData}
        />
      </DocumentTitle>
    );
  }
}

export const mapStateToProps = (
  { editUser, createUser, app }: GlobalState,
  ownProps: Object,
) => {
  return {
    user: editUser.user,
    nonePropertiesSelected: editUser.nonePropertiesSelected,
    selectedProperties: editUser.selectedProperties,
    selectedPropertyGroups: editUser.selectedPropertyGroups,
    hideMultiselect: editUser.hideMultiselect,
    userStatuses: createUser.userStatuses,
    userTitles: createUser.userTitles,
    userRoles: createUser.userRoles,
    properties: createUser.properties,
    userId: ownProps.match.params.userId,
    organizationId: app.currentUser ? app.currentUser.user.organizationId : '',
    isSubmitting: editUser.isSubmitting,
    propertyClasses: app.propertyClasses,
    // Add propertyGroupsData for synchronization
    propertyGroupsData: editUser.propertyGroupsData || [],
  };
};

export function mapDispatchToProps(dispatch: any) {
  return {
    editUserActions: bindActionCreators(editUserActions, dispatch),
    userActions: bindActionCreators(userActions, dispatch),
    appActions: bindActionCreators(appActions, dispatch),
    reduxFormActions: bindActionCreators(
      {
        change,
      },
      dispatch,
    ),
  };
}

const InjectedEditUserPage = injectIntl(withLDConsumer()(EditUserPage));
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InjectedEditUserPage);
